import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import './index.scss'
import html2canvas from 'html2canvas'
import { collapseLeftToolbar } from '../../../../../action/left-toolbar-state-actions'
import { enableMapObject } from '../../../../../action/disable-map-obj-state-action'
import { trackUpdateRequest, trackDeleteRequest } from '../../../../../action/track-actions'
import { photoUploadRequest } from '../../../../../action/photo-actions'
import { userHasPro } from '../../../../../lib/util'
import { buildTrackPoints, computeTrackStats } from '../../../../../lib/trackStats'
import { resolveTrackColor, TRACK_COLORS, FREE_COLOR, TRACK_WIDTHS, normalizeTrackWidth, trackPolylineOptions } from '../../../../../lib/trackColor'
import { applyTrackPreview3D, resetTracks3D, clearTrackGhost3D } from '../../../../threedmap/render-3d-tracks'
import TrackView from './view'
import TrackEdit from './edit'
import DeleteConfirm from './delete-confirm'
import StatCapsule from './stat-capsule'

export const LINE_STYLES = ['dots', 'dashed', 'solid']

const TrackDetails = (props) => {
  const { leftToolbarState, user, userPreferences, mapReference, is3dMapselected, map3d } = props
  const editable = leftToolbarState.editable
  const isPro = userHasPro(user)
  const units = userPreferences?.units || 'imperial'

  // Parse the track's geoJson once per track id. Trim is non-destructive: the stored
  // geometry is the KEPT slice, and only the trimmed-off pieces are backed up in
  // properties.trimHead / trimTail (so the payload never duplicates the whole track).
  // Reconstruct the full original = head + kept + tail, and the kept range within it.
  const parsed = useMemo(() => {
    try {
      const geo = JSON.parse(editable.geoJson)
      const feature = geo.features[0]
      const keptCoords = feature.geometry.coordinates || []
      const keptTimes = Array.isArray(feature.properties.coordTimes) ? feature.properties.coordTimes : null
      const head = feature.properties.trimHead || null
      const tail = feature.properties.trimTail || null
      const headCoords = (head && head.coordinates) || []
      const tailCoords = (tail && tail.coordinates) || []
      const fullCoordinates = [...headCoords, ...keptCoords, ...tailCoords]
      // Rebuild full coordTimes only when every segment's times line up with its coords.
      let fullCoordTimes = null
      if (keptTimes) {
        const headTimes = (head && head.coordTimes) || []
        const tailTimes = (tail && tail.coordTimes) || []
        if (headCoords.length === headTimes.length && tailCoords.length === tailTimes.length) {
          fullCoordTimes = [...headTimes, ...keptTimes, ...tailTimes]
        }
      }
      const initialTrim = [headCoords.length, headCoords.length + keptCoords.length - 1]
      const line = feature.properties.line || {}
      return { geo, feature, fullCoordinates, fullCoordTimes, initialTrim, line, stats: feature.properties.stats || {} }
    } catch (e) {
      return { geo: null, feature: null, fullCoordinates: [], fullCoordTimes: null, initialTrim: [0, 0], line: {}, stats: {} }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable.id])

  const nPts = parsed.fullCoordinates.length
  const allPoints = useMemo(() => buildTrackPoints(parsed.fullCoordinates, parsed.fullCoordTimes), [parsed])

  const [screen, setScreen] = useState('view')
  const [name, setName] = useState(editable.name || 'Track')
  const [notes, setNotes] = useState(editable.notes || '')
  const [color, setColor] = useState(resolveTrackColor(parsed.line))
  const [lineStyle, setLineStyle] = useState(parsed.line.style || 'solid')
  const [lineWidth, setLineWidth] = useState(normalizeTrackWidth(parsed.line.width))
  const [trim, setTrim] = useState(parsed.initialTrim)
  const [styleOpen, setStyleOpen] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [saving, setSaving] = useState(false)
  const trimBackup = useRef(parsed.initialTrim)
  const editBackup = useRef(null)

  // Reset when a different track is opened.
  useEffect(() => {
    setScreen('view')
    setName(editable.name || 'Track')
    setNotes(editable.notes || '')
    setColor(resolveTrackColor(parsed.line))
    setLineStyle(parsed.line.style || 'solid')
    setLineWidth(normalizeTrackWidth(parsed.line.width))
    setTrim(parsed.initialTrim)
    setConfirmDelete(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable.id])

  const trimmed = trim[0] > 0 || trim[1] < nPts - 1

  // Derived points + stats for the current trim range.
  const viewPoints = useMemo(() => allPoints.slice(trim[0], trim[1] + 1), [allPoints, trim])
  const stats = useMemo(() => {
    const coords = parsed.fullCoordinates.slice(trim[0], trim[1] + 1)
    const times = parsed.fullCoordTimes ? parsed.fullCoordTimes.slice(trim[0], trim[1] + 1) : null
    return computeTrackStats(coords, times, parsed.stats)
  }, [parsed, trim])

  // ---- Map polyline helpers ----
  const getPoly = () => { try { return editable.mapObj ? editable.mapObj() : null } catch (e) { return null } }
  const latLngs = (s, e) => {
    const g = typeof google !== 'undefined' ? google : null
    if (!g) return []
    return parsed.fullCoordinates.slice(s, e + 1).map(c => new g.maps.LatLng(c[1], c[0]))
  }
  const ghostRef = useRef(null)
  const hoverMarkerRef = useRef(null)
  const map3dRef = useRef(null)
  map3dRef.current = map3d

  // Highlight a point on the map polyline synced with the chart hover crosshair.
  const setHoverPoint = useCallback((absIdx) => {
    const g = typeof google !== 'undefined' ? google : null
    if (!g || !mapReference) return
    const coord = absIdx == null ? null : parsed.fullCoordinates[absIdx]
    if (!coord) {
      if (hoverMarkerRef.current) { hoverMarkerRef.current.setMap(null); hoverMarkerRef.current = null }
      return
    }
    const pos = new g.maps.LatLng(coord[1], coord[0])
    const icon = { path: g.maps.SymbolPath.CIRCLE, scale: 6, fillColor: color, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 }
    if (!hoverMarkerRef.current) {
      hoverMarkerRef.current = new g.maps.Marker({ map: mapReference, position: pos, icon, clickable: false, zIndex: 30 })
    } else {
      hoverMarkerRef.current.setPosition(pos)
      hoverMarkerRef.current.setIcon(icon)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed, mapReference, color])
  const clearHoverPoint = () => { if (hoverMarkerRef.current) { hoverMarkerRef.current.setMap(null); hoverMarkerRef.current = null } }

  const restylePreview = useCallback(() => {
    const poly = getPoly()
    if (!poly) return
    try {
      poly.setOptions(trackPolylineOptions(color, isPro ? lineStyle : (lineStyle === 'dots' ? 'dots' : lineStyle), lineWidth))
      poly.setPath(latLngs(trim[0], trim[1]))
    } catch (e) { /* map not ready */ }
    // ghost line (full track) while trimming
    const g = typeof google !== 'undefined' ? google : null
    if (g && mapReference) {
      if (trimmed) {
        if (!ghostRef.current) {
          ghostRef.current = new g.maps.Polyline({
            map: mapReference, path: latLngs(0, nPts - 1),
            strokeColor: '#ffffff', strokeOpacity: 0.30, strokeWeight: 2, zIndex: 5, clickable: false
          })
        }
      } else if (ghostRef.current) {
        ghostRef.current.setMap(null); ghostRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, lineStyle, lineWidth, trim, trimmed, isPro, mapReference])

  // Live-preview the edit on the 3D map (the 2D restyle above mutates the hidden
  // Google polyline, which isn't visible in 3D). Updates the MapLibre track source
  // in place — reverted on cancel/close via resetTracks3D.
  const preview3D = useCallback(() => {
    if (!is3dMapselected || !map3d) return
    const style = isPro ? lineStyle : (lineStyle === 'dots' ? 'dots' : lineStyle)
    const coords = parsed.fullCoordinates.slice(trim[0], trim[1] + 1)
    const fullCoords = trimmed ? parsed.fullCoordinates : null
    applyTrackPreview3D(map3d, editable.id, { color, style, width: lineWidth, coords, fullCoords })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is3dMapselected, map3d, color, lineStyle, lineWidth, trim, trimmed, isPro, parsed, editable.id])

  // Apply live restyle only while editing.
  useEffect(() => {
    if (screen === 'edit') { restylePreview(); preview3D() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, color, lineStyle, lineWidth, trim])

  const clearGhost = () => { if (ghostRef.current) { ghostRef.current.setMap(null); ghostRef.current = null } }

  // Restore the original polyline appearance (used on Cancel / unmount without save).
  const restoreMap = useCallback(() => {
    clearGhost()
    const poly = getPoly()
    if (poly && editBackup.current) {
      try {
        poly.setOptions(editBackup.current.options)
        // Restore the saved kept range (not the full track).
        poly.setPath(latLngs(parsed.initialTrim[0], parsed.initialTrim[1]))
      } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed])

  useEffect(() => () => { clearGhost(); clearHoverPoint(); if (map3dRef.current) resetTracks3D(map3dRef.current) }, []) // unmount safety

  // ---- Navigation ----
  const goEdit = () => {
    trimBackup.current = [...trim]
    // Capture the saved appearance so Cancel can restore color + line style + width exactly.
    editBackup.current = { options: trackPolylineOptions(resolveTrackColor(parsed.line), parsed.line.style || 'solid', parsed.line.width) }
    setScreen('edit')
  }
  const cancelEdit = () => {
    setName(editable.name || 'Track')
    setNotes(editable.notes || '')
    setColor(resolveTrackColor(parsed.line))
    setLineStyle(parsed.line.style || 'solid')
    setLineWidth(normalizeTrackWidth(parsed.line.width))
    setTrim([...trimBackup.current])
    restoreMap()
    if (is3dMapselected && map3d) resetTracks3D(map3d)
    setScreen('view')
  }
  const close = () => {
    clearGhost(); clearHoverPoint()
    if (map3d && screen === 'edit') resetTracks3D(map3d)
    props.collapseLeftToolbar(); props.enableMapObject()
  }

  // Free tier: trim snaps back to the captured range on release.
  const onTrimCommit = () => { if (!isPro) setTrim([...trimBackup.current]) }
  const resetTrim = () => setTrim([0, Math.max(0, nPts - 1)])

  // ---- Thumbnail ----
  // Capture a 350x350 PNG centered on the #map element (same approach as the shape
  // editor's map-screenshot thumbnails).
  const takeScreenshot = () => {
    const el = document.getElementById('map')
    if (!el) return Promise.reject(new Error('map element not found'))
    const CW = 350, CH = 350
    const rect = el.getBoundingClientRect()
    const cx = Math.max(0, (rect.width - CW) / 2)
    const cy = Math.max(0, (rect.height - CH) / 2)
    return html2canvas(el, { useCORS: true }).then(canvas => {
      const crop = document.createElement('canvas')
      crop.width = CW; crop.height = CH
      crop.getContext('2d').drawImage(canvas, cx, cy, CW, CH, 0, 0, CW, CH)
      return new Promise((resolve, reject) => {
        crop.toBlob(b => (b ? resolve(b) : reject(new Error('thumbnail blob failed'))), 'image/png')
      })
    })
  }

  // Frame the (restyled, trimmed) track, screenshot it, upload it, and resolve the new
  // photo id — used to replace the track's thumbnail when its appearance changes.
  const captureThumbnail = () => new Promise((resolve, reject) => {
    const g = typeof google !== 'undefined' ? google : null
    const poly = getPoly()
    if (!g || !mapReference || !poly) { resolve(null); return }
    // Show only the final kept + restyled track (no ghost/hover) so the thumbnail
    // matches what gets saved.
    clearGhost()
    clearHoverPoint()
    try {
      poly.setOptions(trackPolylineOptions(color, lineStyle, lineWidth))
      const path = latLngs(trim[0], trim[1])
      poly.setPath(path)
      if (path.length) {
        const bounds = new g.maps.LatLngBounds()
        path.forEach(ll => bounds.extend(ll))
        mapReference.fitBounds(bounds)
      }
    } catch (e) { /* map not ready */ }
    // Let the pan/zoom + tiles settle before capturing.
    setTimeout(() => {
      takeScreenshot()
        .then(blob => {
          const data = new FormData()
          data.append('photo', blob)
          data.append('photoInfo', JSON.stringify({ typeId: 4, name: 'map-screenshot' }))
          return props.photoUploadRequest(data)
        })
        .then(res => resolve(res && res.id ? res.id : null))
        .catch(reject)
    }, 1200)
  })

  // ---- Persistence ----
  const save = () => {
    const geo = JSON.parse(JSON.stringify(parsed.geo))
    const feature = geo.features[0]
    feature.properties.line = { ...(feature.properties.line || {}), color, width: lineWidth, style: lineStyle }

    const full = parsed.fullCoordinates
    const fullTimes = parsed.fullCoordTimes
    const s = trim[0]
    const e = trim[1]
    const isTrim = s > 0 || e < full.length - 1

    if (isTrim) {
      // Persist the KEPT slice as the geometry (what all consumers render) and back up
      // ONLY the trimmed-off head/tail so the full track can be restored later — every
      // point is stored exactly once, so the payload never doubles.
      feature.geometry.coordinates = full.slice(s, e + 1)
      if (fullTimes) feature.properties.coordTimes = fullTimes.slice(s, e + 1)
      feature.properties.stats = computeTrackStats(
        feature.geometry.coordinates, fullTimes ? fullTimes.slice(s, e + 1) : null, parsed.stats
      )
      if (s > 0) {
        feature.properties.trimHead = { coordinates: full.slice(0, s), ...(fullTimes ? { coordTimes: fullTimes.slice(0, s) } : {}) }
      } else { delete feature.properties.trimHead }
      if (e < full.length - 1) {
        feature.properties.trimTail = { coordinates: full.slice(e + 1), ...(fullTimes ? { coordTimes: fullTimes.slice(e + 1) } : {}) }
      } else { delete feature.properties.trimTail }
    } else {
      // Restored to the full track — write it back pristine and drop trim metadata.
      feature.geometry.coordinates = full
      if (fullTimes) feature.properties.coordTimes = fullTimes
      feature.properties.stats = computeTrackStats(full, fullTimes, parsed.stats)
      delete feature.properties.trimHead
      delete feature.properties.trimTail
    }

    // Build an explicit DTO with only the fields the server needs — never echo the
    // server's own created/updated back (that re-persists stale timestamps, incl. the
    // 1969 epoch sentinel; WEB-7014). Mirrors how markers/shapes build their payloads.
    const updated = {
      id: editable.id,
      name,
      notes,
      photoId: editable.photoId,
      userId: user.id,
      geoJson: JSON.stringify(geo),
      measurementTypeId: 1, // track
      public: editable.public ?? false
    }

    const persist = () => {
      // Remove the current polyline so RenderTracks redraws from the saved geoJson.
      clearGhost()
      clearHoverPoint()
      // Drop the 3D ghost; the previewed 3D styling already matches what render-3d-tracks
      // will redraw once the update lands.
      if (map3d) clearTrackGhost3D(map3d)
      const poly = getPoly()
      try { poly && poly.setMap(null) } catch (err) {}
      props.trackUpdateRequest(updated)
        .then(() => { setSaving(false); props.collapseLeftToolbar(); props.enableMapObject() })
        .catch(err => { setSaving(false); console.error('Unable to update track', err) })
    }

    // Regenerate the map thumbnail only when the track's appearance changed
    // (color, line style, or trim), then replace the stored photoId with the new one.
    const appearanceChanged =
      color !== resolveTrackColor(parsed.line) ||
      lineStyle !== (parsed.line.style || 'solid') ||
      lineWidth !== normalizeTrackWidth(parsed.line.width) ||
      s !== parsed.initialTrim[0] || e !== parsed.initialTrim[1]

    // The thumbnail is a screenshot of the 2D #map, which is hidden in 3D — skip the
    // regen when saving from 3D (keep the existing thumbnail rather than a blank one).
    if (appearanceChanged && !is3dMapselected) {
      setSaving(true)
      captureThumbnail()
        .then(photoId => { if (photoId) updated.photoId = photoId })
        .catch(err => console.warn('Track thumbnail regeneration failed', err))
        .then(persist)
    } else {
      persist()
    }
  }

  const doDelete = () => {
    clearGhost()
    clearHoverPoint()
    const poly = getPoly()
    try { poly && poly.setMap(null) } catch (e) {}
    props.trackDeleteRequest(editable.id)
      .then(() => { props.collapseLeftToolbar(); props.enableMapObject() })
      .catch(err => console.error('Unable to delete track', err))
  }

  if (!parsed.feature) return null

  const shared = {
    name, notes, units, isPro, stats, viewPoints, allPoints,
    color, lineStyle, lineWidth, trim, trimmed, nPts,
    startTimeS: stats.startTimeS ?? parsed.stats.startTime,
    stopTimeS: stats.stopTimeS ?? parsed.stats.stopTime
  }

  return (
    <div className="track-details-root">
      {saving && <div className="td-saving"><div className="td-spinner" /></div>}
      {screen === 'view' ? (
        <TrackView
          {...shared}
          onHover={(i) => setHoverPoint(i == null ? null : trim[0] + i)}
          onClose={close}
          onEdit={goEdit}
          onDelete={() => setConfirmDelete(true)}
        />
      ) : (
        <TrackEdit
          {...shared}
          onHover={(i) => setHoverPoint(i == null ? null : i)}
          colors={TRACK_COLORS}
          lineStyles={LINE_STYLES}
          widths={TRACK_WIDTHS}
          styleOpen={styleOpen}
          onToggleStyle={() => setStyleOpen(o => !o)}
          onName={setName}
          onNotes={setNotes}
          onColor={(c) => { if (isPro || c === FREE_COLOR) setColor(c) }}
          onLineStyle={(s) => { if (isPro || s === 'dots') setLineStyle(s) }}
          onWidth={(w) => setLineWidth(w)}
          onTrimChange={(s, e) => setTrim([s, e])}
          onTrimCommit={onTrimCommit}
          onResetTrim={resetTrim}
          onClose={close}
          onCancel={cancelEdit}
          onSave={save}
        />
      )}

      {screen === 'edit' && <StatCapsule stats={stats} units={units} />}

      {confirmDelete && (
        <DeleteConfirm
          name={name}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={doDelete}
        />
      )}
    </div>
  )
}

const mapStateToProps = state => ({
  leftToolbarState: state.leftToolbarState,
  user: state.user,
  userPreferences: state.userPreferences,
  mapReference: typeof state.mapReference === 'function' ? state.mapReference() : null,
  is3dMapselected: state.is3dMapselected,
  map3d: typeof state.mapReferenceThreed === 'function' ? null : state.mapReferenceThreed,
  auth: state.auth
})

const mapDispatchToProps = dispatch => ({
  collapseLeftToolbar: () => dispatch(collapseLeftToolbar()),
  enableMapObject: () => dispatch(enableMapObject()),
  trackUpdateRequest: track => dispatch(trackUpdateRequest(track)),
  trackDeleteRequest: id => dispatch(trackDeleteRequest(id)),
  photoUploadRequest: data => dispatch(photoUploadRequest(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(TrackDetails)
